import { wallClockNow } from '../../../utils/tournament/presentationAgenda';
import {
  IBoardPresentationRoom,
  IBoardRankingGroup,
  IBoardRound,
  IBoardSlot,
  IBoardSnapshot,
} from '../../../dtos/IBoardSnapshot';

/**
 * Torneio simulado para a demo do telão. Gera versões no mesmo formato do
 * `GET /public/boards/:token`, então o telão real não sabe que é demo.
 */

const MIN = 60_000;
const ARENAS = ['Arena A', 'Arena B'];

const GROUPS = [
  { id: 'tm-pintor-1', modality: 'robo_pintor', category: 'category_1' },
  { id: 'tm-linha-2', modality: 'seguidor_de_linha', category: 'category_2' },
  { id: 'tm-pintor-3', modality: 'robo_pintor', category: 'category_3' },
];

const TEAMS: [string, string, number][] = [
  ['RoboTitãs', 'Colégio Santa Maria', 0],
  ['Byte Busters', 'Escola Estadual Dom Pedro II', 0],
  ['Circuito Veloz', 'Colégio Objetivo', 0],
  ['Os Engrenados', 'Instituto Federal', 0],
  ['MicroMakers', 'Colégio Bernoulli', 0],
  ['Pintores Digitais', 'Escola Sesi Contagem', 0],
  ['Linha Certa', 'Colégio Magnum', 1],
  ['Mega Volts', 'Colégio Loyola', 1],
  ['Código Azul', 'Colégio Santa Maria', 1],
  ['Turbo Sensores', 'Escola Municipal Anísio Teixeira', 1],
  ['Fuzzy Bots', 'Colégio Arnaldo', 1],
  ['Pincel Robótico', 'Colégio Santo Agostinho', 2],
  ['Arte em Bits', 'Escola Sesi Betim', 2],
  ['Traço Perfeito', 'Colégio Promove', 2],
  ['Os Inventores', 'Instituto Federal', 2],
  ['Cores do Futuro', 'Colégio Batista', 2],
];

interface ITeam {
  id: string;
  name: string;
  school: string;
  group: number;
  points: number;
  rounds: number;
  arena: string | null;
}

const pick = <T>(items: T[]): T =>
  items[Math.floor(Math.random() * items.length)];
const between = (min: number, max: number) =>
  min + Math.floor(Math.random() * (max - min + 1));

export default class DemoTournament {
  private version = 1;

  private teams: ITeam[];

  private slots: IBoardSlot[] = [];

  private rounds: IBoardRound[] = [];

  private completed = false;

  private roundSeq = 0;

  constructor(private origin = Date.now()) {
    this.teams = TEAMS.map(([name, school, group], i) => ({
      id: `team-${i}`,
      name,
      school,
      group,
      points: 0,
      rounds: 0,
      arena: null,
    }));

    // Duas arenas em paralelo, uma rodada a cada 6 min; primeira em ~2 min.
    // Hora de parede em UTC, como o cronograma real grava.
    const order = [...this.teams, ...this.teams];
    order.forEach((team, i) => {
      const arena = i % ARENAS.length;
      const start =
        wallClockNow(this.origin) + 2 * MIN + Math.floor(i / 2) * 6 * MIN;
      this.slots.push(this.slot(`slot-${i}`, team, ARENAS[arena], start));
    });
  }

  private slot(
    id: string,
    team: ITeam,
    arena: string,
    start: number,
  ): IBoardSlot {
    return {
      slot_id: id,
      round_number: 1,
      starts_at: new Date(start).toISOString(),
      ends_at: new Date(start + 6 * MIN).toISOString(),
      status: 'scheduled',
      is_break: false,
      break_label: null,
      arena_name: arena,
      team_id: team.id,
      team_name: team.name,
      school_name: team.school,
      modality: GROUPS[team.group].modality,
    };
  }

  /** Versão atual, no formato da API. */
  public snapshot(): IBoardSnapshot {
    const ranking: IBoardRankingGroup[] = GROUPS.map((group, index) => {
      const members = this.teams
        .filter(t => t.group === index)
        .sort((a, b) => b.points - a.points || a.name.localeCompare(b.name));

      return {
        tournament_modality_id: group.id,
        modality: group.modality,
        category: group.category,
        // Empate divide a posição (todos em 1º no começo, como no backend).
        entries: members.map(team => ({
          position: 1 + members.filter(o => o.points > team.points).length,
          team_id: team.id,
          team_name: team.name,
          school_name: team.school,
          points: team.points,
          rounds_total: team.points,
          presentation_total: 0,
          presentation_scored: false,
          rounds_scored: team.rounds,
          best_round_time: null,
          arena_name: team.arena,
        })),
      };
    });

    return {
      version: this.version,
      generated_at: new Date().toISOString(),
      tournament: {
        id: 'demo',
        name: 'Torneio FRL · Demonstração',
        stage: 'internal',
        status: this.completed ? 'completed' : 'in_progress',
        date: new Date(wallClockNow(this.origin)).toISOString(),
        end_date: null,
      },
      ranking,
      last_rounds: [...this.rounds].reverse().slice(0, 10),
      upcoming: this.completed
        ? []
        : this.slots.filter(s => s.status !== 'completed').slice(0, 14),
      presentations: this.completed ? [] : this.presentations(),
    };
  }

  /**
   * Sala da Apresentação Técnica: uma equipe a cada 15 min desde o início da
   * demo. Hora de parede em UTC, como o cronograma real grava.
   */
  private presentations(): IBoardPresentationRoom[] {
    const start = wallClockNow(this.origin) - 5 * MIN;
    return [
      {
        room_id: 'sala-demo',
        room_name: 'Sala de Apresentação',
        presentations: this.teams
          .filter(team => team.group === 1)
          .map((team, i) => ({
            slot_id: `apresentacao-${team.id}`,
            team_id: team.id,
            team_name: team.name,
            school_name: team.school,
            category: GROUPS[team.group].category,
            starts_at: new Date(start + i * 15 * MIN).toISOString(),
            ends_at: new Date(start + (i + 1) * 15 * MIN).toISOString(),
          })),
      },
    ];
  }

  private bump(): IBoardSnapshot {
    this.version += 1;
    return this.snapshot();
  }

  private ongoing(): IBoardSlot[] {
    return this.slots.filter(s => !s.is_break && s.status === 'ongoing');
  }

  /** Abre a próxima rodada agendada de uma arena (ou da primeira livre). */
  private openNext(arena?: string | null): IBoardSlot | undefined {
    const busy = new Set(this.ongoing().map(s => s.arena_name));
    const next = this.slots.find(
      s =>
        !s.is_break &&
        s.status === 'scheduled' &&
        (arena ? s.arena_name === arena : !busy.has(s.arena_name)),
    );
    if (next) {
      next.status = 'ongoing';
      next.starts_at = new Date(wallClockNow() - 20_000).toISOString();
    }
    return next;
  }

  /** Antes → ao vivo: as duas arenas abrem a primeira rodada. */
  public start(): IBoardSnapshot {
    ARENAS.forEach(() => this.openNext());
    return this.bump();
  }

  private registerRound(team: ITeam, score: number, arena: string | null) {
    this.roundSeq += 1;
    team.points += score;
    team.rounds += 1;
    team.arena = arena;
    this.rounds.push({
      round_id: `round-${this.roundSeq}`,
      team_id: team.id,
      team_name: team.name,
      school_name: team.school,
      arena_name: arena,
      modality: GROUPS[team.group].modality,
      total_score: score,
      completion_time_seconds: between(48, 170),
      bonus_applied: Math.random() < 0.35,
      scored_at: new Date(Date.now() + this.roundSeq).toISOString(),
    });
  }

  /**
   * Fecha a rodada de uma equipe em andamento e abre a próxima da mesma
   * arena. `score` padrão: pontuação comum, sem mudar o líder de propósito.
   */
  private closeOngoing(slot: IBoardSlot, score?: number) {
    const team = this.teams.find(t => t.id === slot.team_id) ?? this.teams[0];
    slot.status = 'completed';
    this.registerRound(team, score ?? between(35, 70), slot.arena_name);
    this.openNext(slot.arena_name);
  }

  /** Uma rodada encerrada (banner + pontos na tabela). */
  public closeRound(): IBoardSnapshot {
    const slot = this.ongoing()[0] ?? this.openNext();
    if (slot) this.closeOngoing(slot);
    return this.bump();
  }

  /** A última colocada de um grupo pontua o bastante para virar líder. */
  public overtake(): IBoardSnapshot {
    const group = pick([0, 1, 2]);
    const members = this.teams
      .filter(t => t.group === group)
      .sort((a, b) => a.points - b.points);
    const last = members[0];
    const leader = members[members.length - 1];
    const score = Math.max(40, leader.points - last.points + between(12, 30));
    const arena =
      this.slots.find(s => s.team_id === last.id)?.arena_name ?? ARENAS[0];
    this.registerRound(last, score, arena);
    return this.bump();
  }

  /** Duas arenas fecham ao mesmo tempo: banners em fila. */
  public closeSimultaneous(): IBoardSnapshot {
    const open = this.ongoing();
    while (open.length < 2) {
      const next = this.openNext();
      if (!next) break;
      open.push(next);
    }
    open.slice(0, 2).forEach(slot => this.closeOngoing(slot));
    return this.bump();
  }

  /** Começa a próxima rodada (sem fechar outra): destaque "COMEÇOU!". */
  public startNext(): IBoardSnapshot {
    const open = this.ongoing();
    if (open.length >= ARENAS.length) open[0].status = 'completed';
    this.openNext(open.length >= ARENAS.length ? open[0].arena_name : null);
    return this.bump();
  }

  /** Intervalo acontecendo agora. */
  public startBreak(): IBoardSnapshot {
    this.slots = this.slots.filter(s => !s.is_break);
    this.slots.unshift({
      slot_id: `break-${this.version}`,
      round_number: 0,
      starts_at: new Date(wallClockNow() - 10_000).toISOString(),
      ends_at: new Date(wallClockNow() + 10 * MIN).toISOString(),
      status: 'ongoing',
      is_break: true,
      break_label: 'Intervalo para o lanche',
      arena_name: null,
      team_id: null,
      team_name: null,
      school_name: null,
      modality: null,
    });
    return this.bump();
  }

  /** Torneio concluído: resultado final com campeões. */
  public finish(): IBoardSnapshot {
    // Quem não jogou ganha pontos "silenciosos" (sem banner): todo grupo
    // termina com campeã.
    this.teams.forEach(team => {
      if (team.points === 0) team.points = between(20, 60);
    });
    this.completed = true;
    return this.bump();
  }

  public get isLive(): boolean {
    return !this.completed && this.ongoing().length > 0;
  }

  public get isCompleted(): boolean {
    return this.completed;
  }
}
